import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Plus, Trash2, RefreshCw, Search, Globe2, Loader2, Award, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function RankTrackerPage() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [checking, setChecking] = useState({});

  useEffect(() => {
    document.title = "Rank Tracker | Epidemos Admin";
    axios.get(`${BACKEND_URL}/api/sites?status=active`)
      .then(r => setSites(Array.isArray(r.data) ? r.data : r.data.sites || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/rank-tracker/site/${siteId}`);
      setDashboard(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [siteId]);

  const addKeyword = async (e) => {
    e?.preventDefault();
    if (!newKeyword.trim() || !siteId) return;
    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/rank-tracker/add`, {
        site_id: siteId, keyword: newKeyword.trim(), priority: 3,
      });
      setNewKeyword(""); load();
    } finally { setAdding(false); }
  };

  const checkOne = async (id) => {
    setChecking({ ...checking, [id]: true });
    try {
      await axios.post(`${BACKEND_URL}/api/admin/rank-tracker/check/${id}`);
      load();
    } finally { setChecking(prev => { const c = { ...prev }; delete c[id]; return c; }); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Na pewno usunąć?")) return;
    await axios.delete(`${BACKEND_URL}/api/admin/rank-tracker/${id}`);
    load();
  };

  const summary = dashboard?.summary || {};
  const keywords = dashboard?.keywords || [];

  return (
    <div className="p-6 max-w-none mx-auto">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
        <Award className="w-7 h-7 text-blue-600" /> Rank Tracker
      </h1>
      <p className="text-sm text-slate-500 mb-4">Codzienne sprawdzanie pozycji 03:30 (cron). Source: DuckDuckGo/Google CSE/SerpAPI</p>

      {/* Site selector */}
      <div className="mb-6 flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Portal</label>
          <select value={siteId} onChange={e => setSiteId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg">
            <option value="">— wybierz portal —</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>{s.name || s.url}</option>
            ))}
          </select>
        </div>
        <button onClick={load} disabled={!siteId}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className="w-4 h-4" /> Odśwież
        </button>
      </div>

      {siteId && (
        <>
          {/* Stats */}
          {dashboard && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <div className="text-xs text-green-700">TOP 3</div>
                <div className="text-xl font-bold text-green-700">{summary.top3 || 0}</div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-xs text-blue-700">TOP 10</div>
                <div className="text-xl font-bold text-blue-700">{summary.top10 || 0}</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                <div className="text-xs text-slate-700">TOP 30</div>
                <div className="text-xl font-bold text-slate-700">{summary.top30 || 0}</div>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="text-xs text-red-700">Poza TOP</div>
                <div className="text-xl font-bold text-red-700">{summary.not_ranking || 0}</div>
              </div>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                <div className="text-xs text-purple-700">
                  ↑ {summary.rising || 0} / ↓ {summary.falling || 0}
                </div>
                <div className="text-xl font-bold text-purple-700">{dashboard.tracking_count}</div>
              </div>
            </div>
          )}

          {/* Add keyword */}
          <form onSubmit={addKeyword} className="flex gap-2 mb-4">
            <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
              placeholder='Dodaj keyword do trackingu (np. "skarga do urzedu")'
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg" />
            <button type="submit" disabled={adding || !newKeyword.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Dodaj
            </button>
          </form>

          {/* Tabela keywordów */}
          {loading ? (
            <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Ładuję…</div>
          ) : keywords.length === 0 ? (
            <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
              Brak śledzonych keywordów dla tego portalu. Dodaj pierwszy ↑ lub poczekaj — agent dodaje automatycznie tagi z każdego publikowanego artykułu.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left p-3">Keyword</th>
                    <th className="text-center p-3">Aktualna</th>
                    <th className="text-center p-3">Best</th>
                    <th className="text-center p-3">Trend (ostatnie 5)</th>
                    <th className="text-center p-3">Source</th>
                    <th className="text-center p-3">Last check</th>
                    <th className="text-right p-3">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.sort((a, b) => (a.current_position || 999) - (b.current_position || 999)).map(k => {
                    const hist = k.position_history || [];
                    const last5 = hist.slice(-5);
                    return (
                      <tr key={k.id} className="border-t border-slate-100">
                        <td className="p-3 font-medium">{k.keyword}</td>
                        <td className="p-3 text-center">
                          {k.current_position ? (
                            <span className={`inline-block px-2 py-0.5 rounded font-bold text-white ${
                              k.current_position <= 3 ? "bg-green-500" : k.current_position <= 10 ? "bg-blue-500" : k.current_position <= 30 ? "bg-amber-500" : "bg-red-500"
                            }`}>#{k.current_position}</span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3 text-center text-slate-500">{k.best_position ? `#${k.best_position}` : "—"}</td>
                        <td className="p-3 text-center font-mono text-xs">
                          {last5.length > 0 ? last5.map(h => h.position || "—").join(" → ") : "—"}
                        </td>
                        <td className="p-3 text-center text-xs text-slate-400">
                          {last5.length > 0 ? last5[last5.length - 1].source : "—"}
                        </td>
                        <td className="p-3 text-center text-xs text-slate-400">
                          {k.last_checked_at ? k.last_checked_at.substring(0, 10) : "nigdy"}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button onClick={() => checkOne(k.id)} disabled={checking[k.id]}
                            className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded mr-1">
                            {checking[k.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Sprawdź"}
                          </button>
                          <button onClick={() => deleteOne(k.id)}
                            className="px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
